/**
 * What to do with a sync job whose worker never came back.
 *
 * The rule lived inside scripts/sync-worker.ts, which has no exports and so
 * could not be tested at all — 387 lines deciding whether a customer's stuck
 * sync is retried or written off, with nothing asserting either branch. The
 * decision is pure, so it lives here beside syncOutcome.ts and the worker
 * calls it.
 *
 * @module services/mediaApi/reapDecision
 */

/** Attempts before a repeatedly-abandoned job is left failed rather than retried. */
export const MAX_ATTEMPTS = 3;

/** A job is abandoned once it has been 'running' for longer than this. */
export const ABANDONED_AFTER_MS = 3 * 60 * 60 * 1000;

export type ReapDecision =
  | { action: 'requeue'; update: { status: 'queued'; started_at: null } }
  | {
      action: 'fail';
      update: { status: 'failed'; completed_at: string; error_message: string };
    };

/**
 * Requeue while attempts remain — the usual cause is a restart, and the sync
 * itself is idempotent. After that, fail with a message that says what
 * happened, because a job silently left alone reads to the account owner as a
 * sync that is still running and never was.
 */
export const decideReap = (input: {
  attempts: number | null;
  /** Injected so the caller owns the clock; tests are not at the mercy of one. */
  now: Date;
}): ReapDecision => {
  const attempts = input.attempts ?? 0;
  if (attempts < MAX_ATTEMPTS) {
    return { action: 'requeue', update: { status: 'queued', started_at: null } };
  }
  return {
    action: 'fail',
    update: {
      status: 'failed',
      completed_at: input.now.toISOString(),
      error_message: `Abandoned: the worker stopped mid-sync and did not return after ${MAX_ATTEMPTS} attempts.`,
    },
  };
};

/**
 * The cutoff a reaper compares started_at against.
 *
 * Still the rule for a job with NO heartbeat, which is what a worker running
 * code older than migration 095 produces. `isAbandoned` below is what callers
 * should ask; this is kept because that fallback branch needs it and because a
 * bare cutoff is the only thing a SQL-side filter could ever express.
 */
export const abandonedCutoff = (now: Date): string =>
  new Date(now.getTime() - ABANDONED_AFTER_MS).toISOString();

/**
 * How long a heartbeat may go quiet before the job holding it is presumed dead.
 *
 * Twelve times tighter than ABANDONED_AFTER_MS, and that is the point of having
 * a heartbeat at all: the 3-hour figure was never a statement about how long a
 * sync takes, it was the smallest number that would not reap a legitimate long
 * run. A job that is still beating is never reaped however long it runs, so the
 * quiet-threshold is free to be short — a genuinely dead worker is now
 * recovered in minutes rather than hours.
 *
 * Generous against the beat interval on purpose: the worker beats every few
 * minutes, so this tolerates several missed beats before acting. A single
 * failed write, a GC pause or a slow network must not cost a live job its
 * claim.
 */
export const HEARTBEAT_STALE_MS = 15 * 60 * 1000;

/**
 * Whether a RUNNING job should be treated as abandoned.
 *
 * Two branches, and the split is a rollout property rather than a preference:
 *
 * - `heartbeatAt` present — the worker is on code that reports liveness, so
 *   judge it on its last sign of life against the short threshold.
 * - `heartbeatAt` null — no liveness signal exists, either because nothing has
 *   beaten yet in the first interval or because the worker predates migration
 *   095. Fall back to the old rule. Being wrong here costs a false reap after
 *   three hours, exactly as before; the opposite default would reap every job
 *   an old worker claimed, fifteen minutes in.
 *
 * An absent `startedAt` on a running row is not judgeable and returns false: a
 * row with no timestamps is a bug to find, not a job to kill. Unparseable dates
 * are treated the same way, because NaN comparisons are silently false and a
 * guard that fails open on bad data should say so rather than discover it.
 *
 * Shared with the /settings UI (`platformAccountService`) so the badge the
 * account owner reads and the decision the reaper takes cannot disagree — they
 * were two copies of one rule, in two files, with the constant declared twice.
 */
export const isAbandoned = (input: {
  startedAt: string | null;
  heartbeatAt: string | null;
  now: Date;
}): boolean => {
  const signal = input.heartbeatAt ?? input.startedAt;
  if (!signal) return false;
  const at = Date.parse(signal);
  if (Number.isNaN(at)) return false;
  const limit = input.heartbeatAt ? HEARTBEAT_STALE_MS : ABANDONED_AFTER_MS;
  return input.now.getTime() - at > limit;
};
